if (!customElements.get('article-table-of-contents')) {
  customElements.define(
    'article-table-of-contents',
    class ArticleTableOfContents extends HTMLElement {
      connectedCallback() {
        if (this.prepareLayout()) return;
        if (this.initialized) return;

        this.content = this.closest('.article-template__content-layout')?.querySelector('[data-article-content]');
        this.list = this.querySelector('[data-article-toc-list]');
        this.details = this.querySelector('details');
        if (!this.content || !this.list || !this.details) return;

        this.includeH4 = this.dataset.includeH4 === 'true';
        this.headings = [...this.content.querySelectorAll(this.includeH4 ? 'h2, h3, h4' : 'h2, h3')]
          .filter((heading) => heading.textContent.trim() && !heading.closest('[hidden]'));

        if (this.headings.length < 2) return;

        this.initialized = true;
        this.assignHeadingIds();
        this.buildList();
        this.hidden = false;
        this.closest('.article-template__content-layout')?.classList.add('has-table-of-contents');

        if (window.matchMedia('(max-width: 989px)').matches) this.details.open = false;

        this.handleClick = this.handleClick.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.addEventListener('click', this.handleClick);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.handleScroll();
        this.restoreHashPosition();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.removeEventListener('click', this.handleClick);
        window.removeEventListener('scroll', this.handleScroll);
        if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
        this.initialized = false;
      }

      prepareLayout() {
        const source = this.closest('[data-automatic-article-index]');
        const currentLayout = this.closest('.article-template__content-layout');
        if (currentLayout || source?.dataset.placed === 'true') return false;

        const content = document.querySelector('.article-template__content[itemprop="articleBody"]');
        if (!source || !content || content.closest('.article-template__content-layout')) return false;

        const layout = document.createElement('div');
        layout.className = 'article-template__content-layout page-width';
        content.dataset.articleContent = '';
        content.parentNode.insertBefore(layout, content);
        source.dataset.placed = 'true';
        source.hidden = false;
        layout.append(source, content);
        return true;
      }

      assignHeadingIds() {
        const usedIds = new Set([...document.querySelectorAll('[id]')].map((element) => element.id));

        this.headings.forEach((heading, index) => {
          if (heading.id) return;

          const baseId = this.slugify(heading.textContent) || `sezione-${index + 1}`;
          let uniqueId = baseId;
          let suffix = 2;

          while (usedIds.has(uniqueId)) {
            uniqueId = `${baseId}-${suffix}`;
            suffix += 1;
          }

          heading.id = uniqueId;
          usedIds.add(uniqueId);
        });
      }

      slugify(value) {
        return value
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      buildList() {
        const fragment = document.createDocumentFragment();
        this.list.replaceChildren();

        this.headings.forEach((heading) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          const level = Number(heading.tagName.slice(1));

          item.className = 'article-toc__item';
          item.dataset.level = String(level);
          link.className = 'article-toc__link';
          link.href = `#${heading.id}`;
          link.textContent = heading.textContent.trim();
          link.dataset.headingId = heading.id;
          item.appendChild(link);
          fragment.appendChild(item);
        });

        this.list.appendChild(fragment);
        this.links = [...this.list.querySelectorAll('.article-toc__link')];
      }

      handleClick(event) {
        const link = event.target.closest('.article-toc__link');
        if (!link || !this.contains(link)) return;

        const heading = document.getElementById(link.dataset.headingId);
        if (!heading) return;

        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        history.pushState(null, '', `#${heading.id}`);
        this.setActiveLink(heading.id);

        if (window.matchMedia('(max-width: 989px)').matches) this.details.open = false;
      }

      handleScroll() {
        if (this.scrollFrame) return;

        this.scrollFrame = window.requestAnimationFrame(() => {
          const activationLine = Math.min(180, window.innerHeight * 0.24);
          let activeHeading = this.headings[0];

          this.headings.forEach((heading) => {
            if (heading.getBoundingClientRect().top <= activationLine) activeHeading = heading;
          });

          this.setActiveLink(activeHeading.id);
          this.scrollFrame = null;
        });
      }

      setActiveLink(headingId) {
        this.links?.forEach((link) => {
          const isActive = link.dataset.headingId === headingId;
          link.classList.toggle('is-active', isActive);
          if (isActive) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      }

      restoreHashPosition() {
        if (!window.location.hash) return;

        const hashId = decodeURIComponent(window.location.hash.slice(1));
        const target = this.headings.find((heading) => heading.id === hashId);
        if (!target) return;

        window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      }
    }
  );
}
